import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import * as Sentry from '@sentry/react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import { ImpersonationButton } from '~/components/ImpersonationButton';
import { MAIN_CONTENT_ID, SkipLink } from '~/components/SkipLink';
import { UploadDialog } from '~/components/UploadDialog';
import { UploadSnackbar } from '~/components/UploadSnackbar';
import { LoadingPane } from '~/components/loading-pane';
import { AppContextProvider, useFetchAppContext } from '~/contexts/app';
import { UploadProvider } from '~/contexts/upload';
import { setUserLocale } from '~/lib/i18n';
import { isSmDown } from '~/lib/responsive';
import { SideNav } from './components';
import { SidePanel } from './components/SidePanel';
import { SideNavProvider } from './context';

export const MainLayout = () => {
  const { t, i18n } = useTranslation();
  const {
    announcementCount,
    currentNetwork,
    currentUser,
    env,
    impersonating,
    trueUser,
    unreadAnnouncementCount,
  } = useFetchAppContext();
  const location = useLocation();

  const isDeviceSmDown = isSmDown();

  const hideNav = useMemo(() => {
    const { pathname } = location;
    return (
      /^\/networks\/\d+\/studio\/\d+\/design\/\d+/.test(pathname) ||
      /^\/networks\/\d+\/content\/\d+\/(apps|media)\/\d+\/(details|settings|thumbnails|renditions|usage|audit-logs).*$/.test(
        pathname,
      ) ||
      /^\/networks\/\d+\/templates\/\d+\/create$/.test(pathname)
    );
  }, [location]);

  useEffect(() => {
    if (!currentUser || !trueUser) return;

    Sentry.setUser({ email: trueUser.email });
    Sentry.setContext(
      'impersonated_user',
      currentUser.id === trueUser.id ? null : { email: currentUser.email },
    );
  }, [currentUser, trueUser]);

  // Set user's preferred locale when loaded
  useEffect(() => {
    if (currentUser) {
      setUserLocale(currentUser.locale);
    }
  }, [currentUser]);

  if (!currentUser || !currentNetwork || !trueUser) {
    return <LoadingPane in />;
  }

  // Convert i18n locale format (en-US, es-MX) to Luxon format (en, es)
  const luxonLocale = i18n.language.split('-')[0];

  return (
    <AppContextProvider
      announcementCount={announcementCount}
      currentUser={currentUser}
      currentNetwork={currentNetwork}
      env={env}
      impersonating={impersonating ?? false}
      trueUser={trueUser}
      unreadAnnouncementCount={unreadAnnouncementCount}
    >
      <LocalizationProvider
        dateAdapter={AdapterLuxon}
        adapterLocale={luxonLocale}
        localeText={{
          start: t('common.date_picker.start'),
          end: t('common.date_picker.end'),
        }}
      >
        <UploadProvider>
          <SkipLink />
          <ImpersonationButton />
          <Box
            sx={{
              display: 'flex',
              height: '100%',
            }}
          >
            <SideNavProvider initialOpen={!isDeviceSmDown}>
              {!hideNav && <SideNav />}
              <Box
                component="main"
                id={MAIN_CONTENT_ID}
                tabIndex={-1}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1,
                  overflow: 'hidden',
                  // Focused only as the skip link's target; it is not a control.
                  '&:focus': { outline: 'none' },
                }}
              >
                <Outlet />
              </Box>
              <SidePanel />
            </SideNavProvider>
          </Box>
          <UploadDialog />
          <UploadSnackbar />
        </UploadProvider>
      </LocalizationProvider>
    </AppContextProvider>
  );
};
